import { supabase } from "@/infrastructure/supabase/client";
import type {
  FeatureRoadmapVerdictRow,
  RoadmapBranchValue,
  VerdictValue,
} from "@/infrastructure/supabase/database.types";

export interface UpsertVerdictInput {
  featureId: string;
  branch: RoadmapBranchValue;
  verdict: VerdictValue;
}

export const featureRoadmapVerdictsRepository = {
  async list(): Promise<FeatureRoadmapVerdictRow[]> {
    const { data, error } = await supabase
      .from("feature_roadmap_verdicts")
      .select("*")
      .order("updated_at", { ascending: false });
    if (error) throw error;
    return data ?? [];
  },

  async listByBranch(branch: RoadmapBranchValue): Promise<FeatureRoadmapVerdictRow[]> {
    const { data, error } = await supabase
      .from("feature_roadmap_verdicts")
      .select("*")
      .eq("branch", branch);
    if (error) throw error;
    return data ?? [];
  },

  async upsert(input: UpsertVerdictInput): Promise<FeatureRoadmapVerdictRow> {
    // Routes through the Postgres function `upsert_verdict_with_sync`, which
    // atomically applies the cross-board sync rules:
    //   * (all-features, non-mvp)   -> deletes any from-scratch verdict
    //   * (from-scratch, anything)  -> upserts (all-features, mvp)
    const { data, error } = await supabase.rpc("upsert_verdict_with_sync", {
      p_feature_id: input.featureId,
      p_branch: input.branch,
      p_verdict: input.verdict,
    });
    if (error) throw error;
    return data as unknown as FeatureRoadmapVerdictRow;
  },

  async remove(featureId: string, branch: RoadmapBranchValue): Promise<void> {
    const { error } = await supabase
      .from("feature_roadmap_verdicts")
      .delete()
      .eq("feature_id", featureId)
      .eq("branch", branch);
    if (error) throw error;
  },

  subscribeToChanges(onChange: () => void) {
    const channel = supabase
      .channel("feature-roadmap-verdicts-changes")
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "feature_roadmap_verdicts" },
        onChange
      )
      .subscribe();
    return () => {
      void supabase.removeChannel(channel);
    };
  },
};
